/**
 * Edit Report Dialog Component (Reports v2)
 *
 * PATCH equivalent of CreateReportDialog. Same field set: name,
 * description, category, dataset, sharing. Pre-fills from the
 * dashboard being edited; on save the parent should refresh the
 * report list.
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { Loader2 } from 'lucide-react';
import {
  type AnalyseCategory,
  type AnalyseDashboard,
  type Dataset,
} from '../../../lib/api/reporting';
import { IconUploadField } from '../IconUploadField';

type SharedWith = AnalyseDashboard['shared_with'];

interface EditReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  report: AnalyseDashboard | null;
  categories: AnalyseCategory[];
  datasets: Dataset[];
  saving: boolean;
  onSubmit: (payload: Partial<AnalyseDashboard>) => void;
}

export function EditReportDialog({
  open,
  onOpenChange,
  report,
  categories,
  datasets,
  saving,
  onSubmit,
}: EditReportDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<number>(0);
  const [dataset, setDataset] = useState<number>(0);
  const [sharedWith, setSharedWith] = useState<SharedWith>('company');
  const [downloadLock, setDownloadLock] = useState(false);

  // Pre-fill from `report` whenever it changes / dialog opens
  useEffect(() => {
    if (!report) return;
    setName(report.name || '');
    setDescription(report.description || '');
    setCategory(report.category || 0);
    setDataset(report.dataset || 0);
    setSharedWith(report.shared_with || 'company');
    setDownloadLock(Boolean(report.download_lock));
  }, [report, open]);

  const handleSubmit = () => {
    if (!report || !name.trim()) return;
    onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      category: category || undefined,
      dataset: dataset || undefined,
      shared_with: sharedWith,
      download_lock: downloadLock,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Report</DialogTitle>
          <DialogDescription>
            Update the report's name, description, category, dataset, or sharing settings.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Report Name *</Label>
            <Input
              placeholder="Enter report name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              placeholder="Enter description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={category.toString()}
                onValueChange={(value) => setCategory(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">No Category</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id.toString()}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Dataset</Label>
              <Select
                value={dataset.toString()}
                onValueChange={(value) => setDataset(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select dataset" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">No Dataset</SelectItem>
                  {datasets.map((ds) => (
                    <SelectItem key={ds.id} value={ds.id.toString()}>
                      {ds.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Sharing</Label>
            <Select
              value={sharedWith}
              onValueChange={(value) => setSharedWith(value as SharedWith)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="private">Private - Only me</SelectItem>
                <SelectItem value="company">Company - All company users</SelectItem>
                <SelectItem value="company_and_suppliers">Shared with Suppliers</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-start justify-between rounded-md border p-3">
            <div className="space-y-0.5">
              <Label className="text-sm font-medium">Lock downloads</Label>
              <p className="text-xs text-muted-foreground">
                When on, only users from your company can export this report. Shared viewers keep read access but cannot download.
              </p>
            </div>
            <Switch
              checked={downloadLock}
              onCheckedChange={setDownloadLock}
              aria-label="Lock downloads"
            />
          </div>
          <div className="space-y-2">
            <Label>Icon</Label>
            <IconUploadField
              resource="dashboards"
              id={report?.id}
              initialContent={report?.icon_content}
              initialExtension={report?.icon_extension}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!name.trim() || saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
